/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { kebabCase } from 'lodash';

import { Divider, Grid, Typography, Typography2 } from '@coursera/cds-core';
import TrackedLink from '@coursera/event-pulse/react/Link';

import { useEnableProjectEnhancements } from 'bundles/employability-common/feature-flags/hooks/useEnableProjectEnhancements';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { SharedEventTrackingData } from 'bundles/portfolio-projects/components/PortfolioBuilderForm';
import { getPortfolioBuilderGuideTranslations } from 'bundles/portfolio-projects/constants';
import EVENTS from 'bundles/portfolio-projects/events';

export type PortfolioBuilderGuideProps = {
  trackingData?: SharedEventTrackingData;
};

const styles = {
  guidanceSection: css`
    border: 2px solid var(--cds-color-grey-50);
    border-radius: 8px;
  `,
  guidanceTitle: css`
    padding: var(--cds-spacing-400);
    background-color: var(--cds-color-blue-50);

    & > h1 {
      margin-bottom: var(--cds-spacing-150);
    }
  `,
  guidanceContent: css`
    padding: var(--cds-spacing-400);
  `,
  stepNumber: css`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: var(--cds-color-blue-50);
    color: var(--cds-color-blue-900);
    margin-right: var(--cds-spacing-200);
  `,
  stepInstuctions: css`
    margin-bottom: var(--cds-spacing-300);

    h2 {
      color: var(--cds-color-blue-900);
      margin-bottom: var(--cds-spacing-150);
    }
  `,
  guidanceLink: css`
    padding: var(--cds-spacing-300);

    & > a {
      text-align: center;
    }
  `,
};

const PortfolioBuilderGuide = ({ trackingData }: PortfolioBuilderGuideProps) => {
  const {
    PORTFOLIO_BUILDER_GUIDE_TITLE,
    PORTFOLIO_BUILDER_GUIDE_DESCRIPTION,
    PORTFOLIO_BUILDER_GUIDE_LEARN_MORE_LABEL,
    PORTFOLIO_BUILDER_GUIDE_STEP_1_TITLE,
    PORTFOLIO_BUILDER_GUIDE_STEP_1_DESCRIPTION,
    PORTFOLIO_BUILDER_GUIDE_STEP_2_TITLE,
    PORTFOLIO_BUILDER_GUIDE_STEP_2_DESCRIPTION,
    PORTFOLIO_BUILDER_GUIDE_STEP_3_TITLE,
    PORTFOLIO_BUILDER_GUIDE_STEP_3_DESCRIPTION,
  } = getPortfolioBuilderGuideTranslations();
  const enableProjectEnhancements = useEnableProjectEnhancements();
  const learnMoreLink =
    'https://learner.coursera.help/hc/articles/000002029-Using-Projects-on-Coursera-to-showcase-your-skills';
  const guidanceSteps = [
    {
      title: PORTFOLIO_BUILDER_GUIDE_STEP_1_TITLE,
      description: PORTFOLIO_BUILDER_GUIDE_STEP_1_DESCRIPTION,
    },
    {
      title: PORTFOLIO_BUILDER_GUIDE_STEP_2_TITLE,
      description: PORTFOLIO_BUILDER_GUIDE_STEP_2_DESCRIPTION,
    },
    {
      title: PORTFOLIO_BUILDER_GUIDE_STEP_3_TITLE,
      description: PORTFOLIO_BUILDER_GUIDE_STEP_3_DESCRIPTION,
    },
  ];

  return (
    <aside css={styles.guidanceSection}>
      <div css={styles.guidanceTitle}>
        <Typography component="h3" variant="h1semibold">
          {PORTFOLIO_BUILDER_GUIDE_TITLE}
        </Typography>
        <Typography2 component="p" variant="bodyPrimary">
          {PORTFOLIO_BUILDER_GUIDE_DESCRIPTION}
        </Typography2>
      </div>
      <ol css={styles.guidanceContent}>
        {guidanceSteps.map((guidance, index) => (
          <Grid component="li" container key={`guidance-${kebabCase(guidance.title)}`}>
            <Grid container item css={styles.stepNumber} alignContent="center" justifyContent="center">
              <Typography2 aria-hidden="true" component="span" variant="subtitleMedium">
                {index + 1}
              </Typography2>
            </Grid>
            <Grid item xs={9} css={styles.stepInstuctions}>
              <Typography component="p" variant="h2semibold">
                {guidance.title}
              </Typography>
              <Typography2 component="p" variant="bodySecondary">
                {guidance.description}
              </Typography2>
            </Grid>
          </Grid>
        ))}
      </ol>
      {!enableProjectEnhancements && (
        <>
          <Divider />
          <Grid container item alignContent="center" justifyContent="center" css={styles.guidanceLink}>
            <TrackedDiv withVisibilityTracking requireFullyVisible trackingName={EVENTS.portfolioBuilderLearnMore}>
              <TrackedLink
                trackingData={{
                  button: {
                    name: EVENTS.portfolioBuilderLearnMore,
                    linkType: 'external',
                    linkURL: learnMoreLink,
                  },
                  ...trackingData,
                }}
                href={learnMoreLink}
                target="_blank"
                variant="quiet"
                typographyVariant="subtitleMedium"
              >
                {PORTFOLIO_BUILDER_GUIDE_LEARN_MORE_LABEL}
              </TrackedLink>
            </TrackedDiv>
          </Grid>
        </>
      )}
    </aside>
  );
};

export default PortfolioBuilderGuide;
